import * as api from '../api';
import * as SiteConfig from '../config/SiteConfig';
import { currentUserURL } from '../config/UserAPIConfig';
import { LoggedOutModal } from '../generic/Modals';
import type { User } from '../model/types';
import { clearToken, getToken, onTokenChange, setToken, setUser } from './authToken';
import fetchOauthToken, { revokeOauthToken } from './fetchOauthToken';
import syncBlock from './syncBlock';

/**
 * Get the current users access token, or `null` if it is not set.
 */
export function getAccessToken(): string | null {
    return getToken()?.accessToken.value ?? null;
}

/**
 * Refresh the access token using the refresh token.
 * @returns `true` if it was refreshed, `false` if it failed to refresh.
 */
export const refreshAccessToken = syncBlock('refreshAccessToken', async () => {
    const token = getToken();
    if (!token) return false;

    try {
        const { accessToken, refreshToken } = await fetchOauthToken({
            grant_type: 'refresh_token',
            refresh_token: token.refreshToken.value,
        });
        setToken(accessToken, refreshToken);
        return true;
    } catch (error) {
        if (error instanceof api.ApiResponseError) {
            console.error('Refresh token not valid', error);
            clearToken();
        } else {
            console.error('Network error while refreshing token', error);
        }
        return false;
    }
});

export async function login(username: string, password: string) {
    const { accessToken, refreshToken } = await fetchOauthToken({
        grant_type: 'password',
        username,
        password,
        device_id: getDeviceId(),
    });
    setToken(accessToken, refreshToken);

    await refreshUser();
}

export const refreshUser = syncBlock('refreshUser', async () => {
    const user = await api.get<User>(currentUserURL());
    setUser(user);
});

export function onUserChange(onChange: (user: User | null) => void): () => void {
    return onTokenChange((t) => onChange(t?.user ?? null));
}

export async function logout() {
    const token = getToken();
    if (token) {
        await revokeOauthToken({
            token: token.refreshToken.value,
            token_type_hint: 'refresh_token',
        }).catch((e) => console.error(e));
    }
    clearToken();
}

export function handleUnauthorizedError() {
    // Make sure the token is gone, so we don't get more errors
    clearToken();

    // Trigger the Logged Out modal window
    LoggedOutModal.call();
}

export function handleNotAuthenticated() {
    // Make sure the token is gone, so we don't get more errors
    clearToken();

    // Redirect to login page, unless we are on some certain pages
    switch (window.location.pathname) {
        // copied from notRedirectedPages used in MissingEmailVerificationFilter.java
        case '/logout':
        case '/login':
        case '/personvern':
            return;
        default:
            location.assign(SiteConfig.loginURLWithRedirect());
    }
}

/**
 * Get a unique identifier for this device.
 * Uses the user-agent string and the timestamp of login.
 * Some common user agent pieces are removed and it is trimmed down to 255 characters long
 * so it will fit in the database column in the backend system
 */
function getDeviceId() {
    return `${navigator.userAgent} ${new Date().toISOString()}`.replace('Mozilla/5.0 ', '').replace('(KHTML, like Gecko) ', '').substring(0, 255);
}
